/* Inspired by https://github.com/gurov/cache-observable */

import { Observable, timer, EMPTY } from 'rxjs';
import { shareReplay, first } from 'rxjs/operators';

/**
 * Cache for observable objects
 */
let cache: {
    [index: string]: Observable<unknown>
} = {};

/**
 * Resets the cache.
 * Should be used for testing purposes only.
 */
export function resetCache() {
    cache = {};
}

/**
 * Calc hash of string
 * Source: https://stackoverflow.com/a/7616484
 */
const hashCode = function (s: string): string {
    let hash: number = 0;
    let chr: number;
    if (s.length === 0) {
        return hash.toString(36);
    }

    for (let i = 0; i < s.length; i++) {
        chr = s.charCodeAt(i);
        // tslint:disable:no-bitwise
        hash = ((hash << 5) - hash) + chr;
        hash |= 0;
    }
    return hash.toString(36);
};

/**
 * Deletes an entry from the cache by key
 */
const cleanUp = (key: string) => {
    delete cache[key];
};

/**
 * Cache Observable function for ms milliseconds
 * @param {number} expiresInMs
 */
export function CacheObservable(expiresInMs?: number) {

    return function (target: object, methodName: string, descriptor: PropertyDescriptor) {

        const original: (...args: unknown[]) => Observable<unknown> = descriptor.value;
        const targetName = target.constructor.name;

        descriptor.value = function (...args: unknown[]) {

            const key = hashCode(`${targetName}:${methodName}:${JSON.stringify(args)}`);

            const entry = cache[key];
            if (entry) {
                return entry;
            }

            const expired = expiresInMs
                ? timer(expiresInMs)
                    .pipe(first())
                : EMPTY;

            cache[key] = original.apply(this, args)
                .pipe(
                    shareReplay(1),
                );

            expired.subscribe(() => cleanUp(key));

            return cache[key];
        };

        return descriptor;
    };
}
